import React from "react";
import { usePublicConfig } from "./PublicConfigProvider";
import {
  adultParticipantGroup,
  formatIsoDate,
  DEFAULT_BACKGROUND_CHECK_URL,
  DEFAULT_BACKGROUND_CHECK_DEADLINE,
} from "../config";

// Round 48 — adult-participant notice on Step 5. Owner-approved wording,
// reproduced exactly; only {Program}, {start} and the background-check
// deadline are filled in, all from the program's config. It is a notice and
// nothing more: no field, no checkbox, no effect on completion % or submit.
//
// The agreement is served by the existing /templates route, which picks the
// program's own copy (MDBS or MDGS) from PROGRAM. It is for review only —
// the participant signs it in person; it is never uploaded.
const AGREEMENT_HREF = "/templates/adult-participant-agreement.pdf";

function AgreementLink() {
  return (
    <p className="mt-2">
      <a className="underline text-program" href={AGREEMENT_HREF} target="_blank" rel="noopener">
        Download the Adult Participant Agreement to review
      </a>{" "}
      <em>(for review only; do not sign or upload it)</em>
    </p>
  );
}

export function AdultParticipantNotice({ dob }: { dob: string }) {
  const cfg = usePublicConfig();
  const group = adultParticipantGroup(dob, cfg.sessionStartDate, cfg.sessionEndDate);
  if (group === null) return null;

  const program = cfg.programName;

  if (group === "B") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-slate-800" data-adult-notice="B">
        <p className="font-semibold">Turning 18 during the session</p>
        <p className="mt-1">
          Based on the date of birth entered, you will turn 18 during {program}. On the morning of
          your birthday, before activities begin, you will personally sign the Adult Participant
          Agreement with a staff member as witness. It covers the liability release, medical
          authorization, media consent, and youth-protection rules for adult participants. Signing it
          is required to continue participating after your birthday.
        </p>
        <AgreementLink />
        <p className="mt-2">
          You may wish to review this agreement with an attorney of your choosing before the session.
        </p>
      </div>
    );
  }

  const start = formatIsoDate(cfg.sessionStartDate);
  const deadline = formatIsoDate(cfg.backgroundCheckDeadline || DEFAULT_BACKGROUND_CHECK_DEADLINE);
  const checkUrl = cfg.backgroundCheckUrl || DEFAULT_BACKGROUND_CHECK_URL;

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-slate-800" data-adult-notice="A">
      <p className="font-semibold">Participants age 18 or older</p>
      <p className="mt-1">
        Based on the date of birth entered, you will be 18 or older when {program} begins on {start}.
        Two additional requirements apply to you.
      </p>
      <p className="mt-3">
        <strong>1. Adult Participant Agreement.</strong> As an adult, you must personally sign the
        Adult Participant Agreement at registration, with a staff member as witness. It covers the
        liability release, medical authorization, media consent, and youth-protection rules for adult
        participants, and takes the place of your parent's consents for those items. Signing it is
        required to participate.
      </p>
      <AgreementLink />
      <p className="mt-2">
        You may wish to review this agreement with an attorney of your choosing before the session.
      </p>
      <p className="mt-3">
        <strong>2. Background check.</strong> Participants who are 18 or older must complete a
        background check through Protect Youth Sports, our screening provider, by{" "}
        <strong>{deadline}</strong>. Protect Youth Sports charges a small fee (currently about
        $16.50), paid directly to them. We receive only a pass/fail result.
      </p>
      <p className="mt-2">
        <a className="underline text-program" href={checkUrl} target="_blank" rel="noopener">
          Start your background check
        </a>
      </p>
    </div>
  );
}
